<script lang="ts">
    import Button from '../Button.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import * as t from '../../translations/Common.ts';

    export let user: {
        firstName: string;
        lastName: string;
        email: string;
        address?: {
            street?: string;
            city?: string;
            state?: string;
            zipCode?: string;
        };
        businessAddress?: {
            street?: string;
            city?: string;
            state?: string;
            zipCode?: string;
        };
    } | null = null;

    export let onUseInfo: () => void = () => {};
    export let onEdit: () => void = () => {};
    export let onNotMe: () => void = () => {};

    $: fullName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    $: displayFirstName = user?.firstName?.trim() || fullName.split(' ')[0] || 'You';
    $: initials = user
        ? `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.trim() || displayFirstName.charAt(0).toUpperCase()
        : '';

    function formatAddress(address?: { street?: string; city?: string; state?: string; zipCode?: string }): string {
        if (!address) return '';

        const street = address.street?.trim() || '';
        const city = address.city?.trim() || '';
        const state = address.state?.trim() || '';
        const zipCode = address.zipCode?.trim() || '';
        const locality = [city, state].filter(Boolean).join(', ');
        const localityWithZip = [locality, zipCode].filter(Boolean).join(' ');

        return [street, localityWithZip].filter(Boolean).join(', ');
    }

    $: addressLine = formatAddress(user?.address) || formatAddress(user?.businessAddress);
</script>

{#if user}
    <!-- Neutral shell: tenant primary only on the CTA (avoids clashing brand colors on teal tints). -->
    <div class="remember-banner w-full max-w-[426px] mr-auto bg-white !border !border-gray-200 !rounded-xl !p-4 !mb-6 shadow-sm" data-testid="remember-me-banner">
        <div class="flex items-start gap-3">
            <!-- Avatar: neutral fill, ring uses org primary (same token as Sign as CTA) -->
            <div class="flex-shrink-0 !w-11 !h-11 !rounded-full flex items-center justify-center !bg-gray-100 !text-gray-700 !font-semibold !text-base !leading-none border border-solid [border-color:var(--vv-action-center-button-color,#64748b)] box-border" data-testid="user-avatar">
                {initials}
            </div>
            
            <!-- User Info -->
            <div class="flex-1 min-w-0" data-testid="user-info">
                <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                        <span class="!text-sm !leading-5 !text-gray-500 !font-medium block" data-testid="welcome-back-label">{t.Welcome_Back()}</span>
                        {#if fullName}
                            <h3 class="!text-lg !leading-6 !font-medium !text-gray-900 !m-0 !mt-0.5 !p-0" data-testid="user-full-name">{fullName}</h3>
                        {/if}
                    </div>
                    <button
                        class="edit-info-top-btn !text-sm !leading-5 !font-medium inline-flex items-center gap-1.5"
                        on:click={onEdit}
                        data-testid="edit-info-button"
                    >
                        <svg class="w-[14px] h-[14px]" viewBox="0 0 640 640" fill="currentColor" aria-hidden="true">
                            <path d="M505 122.9L517.1 135C526.5 144.4 526.5 159.6 517.1 168.9L488 198.1L441.9 152L471 122.9C480.4 113.5 495.6 113.5 504.9 122.9zM273.8 320.2L408 185.9L454.1 232L319.8 366.2C316.9 369.1 313.3 371.2 309.4 372.3L250.9 389L267.6 330.5C268.7 326.6 270.8 323 273.7 320.1zM437.1 89L239.8 286.2C231.1 294.9 224.8 305.6 221.5 317.3L192.9 417.3C190.5 425.7 192.8 434.7 199 440.9C205.2 447.1 214.2 449.4 222.6 447L322.6 418.4C334.4 415 345.1 408.7 353.7 400.1L551 202.9C579.1 174.8 579.1 129.2 551 101.1L538.9 89C510.8 60.9 465.2 60.9 437.1 89zM152 128C103.4 128 64 167.4 64 216L64 488C64 536.6 103.4 576 152 576L424 576C472.6 576 512 536.6 512 488L512 376C512 362.7 501.3 352 488 352C474.7 352 464 362.7 464 376L464 488C464 510.1 446.1 528 424 528L152 528C129.9 528 112 510.1 112 488L112 216C112 193.9 129.9 176 152 176L264 176C277.3 176 288 165.3 288 152C288 138.7 277.3 128 264 128L152 128z"/>
                        </svg>
                        <span>{t.Edit_Info()}</span>
                    </button>
                </div>
                {#if user.email}
                    <p class="!text-sm !leading-5 !text-gray-700 !m-0 !mt-2" data-testid="user-email">{user.email}</p>
                {/if}
                {#if addressLine}
                    <p class="!text-sm !leading-5 !text-gray-700 !m-0 !mt-1" data-testid="user-address">{addressLine}</p>
                {/if}
            </div>
        </div>
        
        <!-- Actions -->
        <div class="flex flex-col gap-3 mt-3" data-testid="remember-me-actions">
            <Button 
                type="action-center"
                class="w-full !min-h-[44px] justify-center"
                on:click={onUseInfo}
                data-testid="continue-as-button"
            >
                <div class="flex items-center justify-center gap-2">
                    <span>{t.Continue_As(displayFirstName)}</span>
                    <CaretRightIcon color="currentColor" />
                </div>
            </Button>
            
            <!-- Secondary action: lower emphasis under primary CTA -->
            <div class="flex flex-wrap items-center justify-center gap-2">
                <button 
                    class="px-4 !min-h-[40px] !text-sm !leading-5 !font-medium !text-gray-500 hover:!text-gray-700 hover:underline focus-visible:underline transition-colors"
                    on:click={onNotMe}
                    data-testid="not-me-button"
                >
                    {t.Not_Me()}
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
    .remember-banner :global(h3[data-testid="user-full-name"]) {
        font-size: 1.125rem !important;
        line-height: 1.5rem !important;
        font-weight: 600 !important;
        color: #111827 !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    :global(.remember-banner button.edit-info-top-btn) {
        color: #374151 !important;
        background: transparent !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        cursor: pointer !important;
    }

    :global(.remember-banner button.edit-info-top-btn:hover) {
        color: #111827 !important;
        text-decoration: underline;
    }
</style>
